import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { gql, useMutation, useQuery } from '@apollo/client';
import { updateUser } from '../store/authSlice';
import { Country, State, City } from 'country-state-city';
import ImageUploadDialogs from './ImageUploadDialogs';

// Custom Tooltip Component
const InfoTooltip = ({ children, message }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 z-50">
          <div className="bg-bulky-navy-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg border border-bulky-navy-600 whitespace-nowrap">
            <div className="flex items-center gap-2">
              <span className="material-icons-round text-xs">info</span>
              <span>{message}</span>
            </div>
            {/* Arrow */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-bulky-navy-800"></div>
          </div>
        </div>
      )}
    </div>
  );
};

const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      username
      email
      mobile
      role
      firstName
      lastName
      ownerName
      companyName
      businessIndustry
      address_line_1
      address_line_2
      city
      state
      country
      zip_code
      profile_image
      email_verified
      email_verified_at
      created_at
      updated_at
    }
  }
`;

const SEND_EMAIL_VERIFICATION_OTP = gql`
  mutation SendEmailVerificationOTP($email: String!) {
    sendEmailVerificationOTP(email: $email) {
      success
      message
      expiresIn
    }
  }
`;

const VERIFY_EMAIL_OTP = gql`
  mutation VerifyEmailOTP($email: String!, $otp: String!) {
    verifyEmailOTP(email: $email, otp: $otp) {
      success
      message
      user {
        id
        username
        email
        mobile
        role
        firstName
        lastName
        ownerName
        companyName
        businessIndustry
        address_line_1
        address_line_2
        city
        state
        country
        zip_code
        profile_image
        email_verified
        email_verified_at
        created_at
        updated_at
      }
    }
  }
`;

const UPLOAD_PROFILE_IMAGE = gql`
  mutation UploadProfileImage($imageData: String!, $fileName: String!, $mimeType: String!) {
    uploadProfileImage(imageData: $imageData, fileName: $fileName, mimeType: $mimeType) {
      success
      message
      imageUrl
    }
  }
`;

const DELETE_PROFILE_IMAGE = gql`
  mutation DeleteProfileImage {
    deleteProfileImage {
      success
      message
      imageUrl
    }
  }
`;

// GraphQL query to fetch fresh user data
const GET_USER = gql`
  query GetUser($id: ID!) {
    user(id: $id) {
      id
      username
      email
      mobile
      role
      firstName
      lastName
      ownerName
      companyName
      businessIndustry
      address_line_1
      address_line_2
      city
      state
      country
      zip_code
      profile_image
      email_verified
      email_verified_at
      created_at
      updated_at
    }
  }
`;

const ProfileManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [updateUserMutation] = useMutation(UPDATE_USER);
  const [sendEmailOTP] = useMutation(SEND_EMAIL_VERIFICATION_OTP);
  const [verifyEmailOTP] = useMutation(VERIFY_EMAIL_OTP);
  const [uploadProfileImage] = useMutation(UPLOAD_PROFILE_IMAGE);
  const [deleteProfileImage] = useMutation(DELETE_PROFILE_IMAGE);

  const [isEditing, setIsEditing] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  // Image upload states
  const [showImageUploadDialog, setShowImageUploadDialog] = useState(false);
  const [showImageConfirmDialog, setShowImageConfirmDialog] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  
  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Add query hook to fetch fresh user data
  const { data: freshUserData, refetch: refetchUser } = useQuery(GET_USER, {
    variables: { id: user?.id },
    skip: !user?.id,
    fetchPolicy: 'network-only' // Always fetch fresh data
  });

  // Update Redux when fresh user data is fetched
  useEffect(() => {
    if (freshUserData?.user) {
      console.log('🔄 Refreshed user data:', freshUserData.user);
      console.log('🔄 Email verified status:', freshUserData.user.email_verified);
      dispatch(updateUser(freshUserData.user));
    }
  }, [freshUserData, dispatch]);

  // Debug: Log user data to see what we're getting
  useEffect(() => {
    console.log('🔍 Current user data in Redux:', user);
    if (user) {
      console.log('📝 User fields:', {
        firstName: user.firstName,
        lastName: user.lastName,
        ownerName: user.ownerName,
        companyName: user.companyName,
        businessIndustry: user.businessIndustry
      });

      // Auto-refresh if data seems stale (empty fields that should have data)
      if (!user.firstName && !user.lastName && !user.ownerName) {
        console.log('🔄 Data seems stale, auto-refreshing...');
        refetchUser();
      }
    }
  }, [user, refetchUser]);

  // Email verification states
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailOTP, setEmailOTP] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [emailLoading, setEmailLoading] = useState(false);

  // Location data for dropdowns
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Auto-detect country from mobile number
  const getCountryFromMobile = (mobile) => {
    if (!mobile) return 'IN'; // Default to India

    // Remove any non-digit characters
    const cleanMobile = mobile.replace(/\D/g, '');

    // Check country codes
    if (cleanMobile.startsWith('91')) return 'IN'; // India
    if (cleanMobile.startsWith('1')) return 'US'; // USA/Canada
    if (cleanMobile.startsWith('44')) return 'GB'; // UK
    if (cleanMobile.startsWith('61')) return 'AU'; // Australia
    if (cleanMobile.startsWith('33')) return 'FR'; // France
    if (cleanMobile.startsWith('49')) return 'DE'; // Germany
    if (cleanMobile.startsWith('81')) return 'JP'; // Japan
    if (cleanMobile.startsWith('86')) return 'CN'; // China

    return 'IN'; // Default to India
  };

  // Get country name for display
  const getCountryName = (countryCode) => {
    const country = countries.find(c => c.isoCode === countryCode);
    return country ? country.name : 'India';
  };

  // Handle input changes properly
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const [formData, setFormData] = useState({
    mobile: user?.mobile || '',
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    ownerName: user?.ownerName || '',
    companyName: user?.companyName || '',
    businessIndustry: user?.businessIndustry || '',
    address_line_1: user?.address_line_1 || '',
    address_line_2: user?.address_line_2 || '',
    city: user?.city || '',
    state: user?.state || '',
    country: user?.country || getCountryFromMobile(user?.mobile),
    zip_code: user?.zip_code || '',
  });

  useEffect(() => {
    if (user) {
      const detectedCountry = getCountryFromMobile(user.mobile);
      // Convert country name to country code if needed
      let countryCode = user.country;
      if (user.country === 'India') countryCode = 'IN';
      if (user.country === 'United States') countryCode = 'US';
      // Add more mappings as needed

      setFormData({
        mobile: user.mobile || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        ownerName: user.ownerName || '',
        companyName: user.companyName || '',
        businessIndustry: user.businessIndustry || '',
        address_line_1: user.address_line_1 || '',
        address_line_2: user.address_line_2 || '',
        city: user.city || '',
        state: user.state || '',
        country: countryCode || detectedCountry,
        zip_code: user.zip_code || '',
      });
    }
  }, [user]);

  // Load countries on component mount
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (formData.country) {
      const stateList = State.getStatesOfCountry(formData.country);
      setStates(stateList);
      setCities([]); // Clear cities when country changes

      // Don't reset state/city if we're just loading initial data
      if (user?.country !== formData.country && user?.state) {
        setFormData(prev => ({ ...prev, state: '', city: '' }));
      }
    }
  }, [formData.country, user?.country, user?.state]);

  // Load cities when state changes
  useEffect(() => {
    if (formData.country && formData.state) {
      const cityList = City.getCitiesOfState(formData.country, formData.state);
      setCities(cityList);

      // Don't reset city if we're just loading initial data
      if (user?.state !== formData.state && user?.city) {
        setFormData(prev => ({ ...prev, city: '' }));
      }
    }
  }, [formData.country, formData.state, user?.state, user?.city]);

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);

  // Email verification handlers
  const handleEmailChange = () => {
    // Only show dialog if email is actually different
    if (formData.email && formData.email !== user?.email) {
      setNewEmail(formData.email);
      setShowEmailDialog(true);
    }
  };

  const handleEmailConfirm = async () => {
    if (!newEmail) {
      setShowEmailDialog(false);
      return;
    }

    setEmailLoading(true);
    try {
      const { data } = await sendEmailOTP({
        variables: { email: newEmail }
      });

      if (data.sendEmailVerificationOTP.success) {
        setShowEmailDialog(false);
        setShowOTPDialog(true);
        setOtpTimer(3600); // 1 hour
        setEmailOTP('');
        // No alert - dialog flow handles the UX
      }
    } catch (error) {
      console.error('Email verification error:', error);
      // Keep it simple - just log error, dialog will show the issue
    } finally {
      setEmailLoading(false);
    }
  };

  const handleOTPVerify = async () => {
    if (!emailOTP || emailOTP.length !== 6) {
      return; // Just return, don't show alert
    }

    setEmailLoading(true);
    try {
      const { data } = await verifyEmailOTP({
        variables: { email: newEmail, otp: emailOTP }
      });

      console.log('📧 Email verification response:', data);

      if (data.verifyEmailOTP.success) {
        console.log('📧 Email verification successful!');
        console.log('📧 Response data:', data.verifyEmailOTP);
        
        // Update Redux store with the returned user data
        if (data.verifyEmailOTP.user) {
          console.log('📧 Updating Redux with user data:', data.verifyEmailOTP.user);
          console.log('📧 User email_verified field:', data.verifyEmailOTP.user.email_verified);
          dispatch(updateUser(data.verifyEmailOTP.user));
          
          // Force refetch to update cache and ensure UI shows verified status
          console.log('📧 Refetching user data to update cache...');
          await refetchUser();
        } else {
          console.log('📧 WARNING: No user data returned from email verification mutation');
        }

        setFormData(prev => ({ ...prev, email: newEmail }));
        setShowOTPDialog(false);
        setEmailOTP('');
        setOtpTimer(0);
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      // Just log error, don't show alert
    } finally {
      setEmailLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Image upload functions
  const handleImageClick = useCallback(() => {
    if (user?.profile_image) {
      // If user has an image, show confirmation dialog
      setShowImageConfirmDialog(true);
    } else {
      // If no image, directly open upload dialog
      setShowImageUploadDialog(true);
    }
  }, [user?.profile_image]);

  const handleImageFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage('Invalid file type. Only JPG and PNG images are allowed.');
      setShowErrorModal(true);
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setErrorMessage('File size too large. Maximum size allowed is 5MB.');
      setShowErrorModal(true);
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async () => {
    if (!imageFile) return;

    setImageUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result;

          const { data } = await uploadProfileImage({
            variables: {
              imageData: base64Data,
              fileName: imageFile.name,
              mimeType: imageFile.type
            }
          });

          if (data.uploadProfileImage.success) {
            // Update Redux store with new image URL
            const imageUrl = data.uploadProfileImage.imageUrl;
            // Add timestamp to prevent caching issues
            const imageUrlWithTimestamp = `${imageUrl}?t=${Date.now()}`;

            const updatedUser = {
              ...user,
              profile_image: imageUrlWithTimestamp
            };
            dispatch(updateUser(updatedUser));

            // Force a global re-render by dispatching a custom event
            window.dispatchEvent(new CustomEvent('profileImageUpdated', {
              detail: { imageUrl: imageUrlWithTimestamp }
            }));

            setShowImageUploadDialog(false);
            setImageFile(null);
            setImagePreview(null);
            console.log('✅ Profile image uploaded successfully');

            // Show success notification
            if (typeof window !== 'undefined') {
              const toast = document.createElement('div');
              toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-sm';
              toast.innerHTML = `
                <div class="flex items-center gap-2">
                  <span class="material-icons-round text-sm">check_circle</span>
                  <div>
                    <div class="font-normal">Success!</div>
                    <div class="text-sm opacity-90">Profile image updated successfully</div>
                  </div>
                </div>
              `;

              document.body.appendChild(toast);

              setTimeout(() => {
                if (document.body.contains(toast)) {
                  document.body.removeChild(toast);
                }
              }, 3000);
            }
          } else {
            throw new Error(data.uploadProfileImage.message || 'Failed to upload image');
          }
        } catch (error) {
          console.error('❌ Image upload error:', error);

          // Show error notification
          if (typeof window !== 'undefined') {
            const toast = document.createElement('div');
            toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-sm';
            toast.innerHTML = `
              <div class="flex items-center gap-2">
                <span class="material-icons-round text-sm">error</span>
                <div>
                  <div class="font-normal">Upload Failed</div>
                  <div class="text-sm opacity-90">${error.message || 'Please try again'}</div>
                </div>
              </div>
            `;

            document.body.appendChild(toast);

            setTimeout(() => {
              if (document.body.contains(toast)) {
                document.body.removeChild(toast);
              }
            }, 5000);
          }
        }
      };
      reader.readAsDataURL(imageFile);
    } catch (error) {
      console.error('❌ Image upload error:', error);
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageDelete = async () => {
    setImageUploading(true);
    try {
      const { data } = await deleteProfileImage();

      if (data.deleteProfileImage.success) {
        // Update Redux store to remove image
        const updatedUser = {
          ...user,
          profile_image: null
        };
        dispatch(updateUser(updatedUser));

        // Force a global re-render by dispatching a custom event
        window.dispatchEvent(new CustomEvent('profileImageUpdated', {
          detail: { imageUrl: null }
        }));

        setShowImageConfirmDialog(false);
        console.log('✅ Profile image deleted successfully');

        // Show success notification
        if (typeof window !== 'undefined') {
          const toast = document.createElement('div');
          toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-sm';
          toast.innerHTML = `
            <div class="flex items-center gap-2">
              <span class="material-icons-round text-sm">check_circle</span>
              <div>
                <div class="font-normal">Success!</div>
                <div class="text-sm opacity-90">Profile image removed successfully</div>
              </div>
            </div>
          `;

          document.body.appendChild(toast);

          setTimeout(() => {
            if (document.body.contains(toast)) {
              document.body.removeChild(toast);
            }
          }, 3000);
        }
      } else {
        throw new Error(data.deleteProfileImage.message || 'Failed to delete image');
      }
    } catch (error) {
      console.error('❌ Image delete error:', error);

      // Show error notification
      if (typeof window !== 'undefined') {
        const toast = document.createElement('div');
        toast.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-sm';
        toast.innerHTML = `
          <div class="flex items-center gap-2">
            <span class="material-icons-round text-sm">error</span>
            <div>
              <div class="font-normal">Delete Failed</div>
              <div class="text-sm opacity-90">${error.message || 'Please try again'}</div>
            </div>
          </div>
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 5000);
      }
    } finally {
      setImageUploading(false);
    }
  };

  // Listen for profile image updates to force re-render
  useEffect(() => {
    const handleProfileImageUpdate = () => {
      setForceUpdate(prev => prev + 1);
    };

    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);
    return () => {
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
    };
  }, []);

  // Listen for profile image click from global header
  useEffect(() => {
    const handleGlobalProfileImageClick = () => {
      handleImageClick();
    };

    window.addEventListener('profileImageClick', handleGlobalProfileImageClick);
    return () => {
      window.removeEventListener('profileImageClick', handleGlobalProfileImageClick);
    };
  }, [handleImageClick]); // Include handleImageClick in dependencies

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...formData
      };

      const { data } = await updateUserMutation({
        variables: {
          id: user.id,
          input: updateData
        }
      });

      // Update Redux store and localStorage
      dispatch(updateUser(data.updateUser));
      setIsEditing(false);

      // No need to refresh - Redux will update the UI automatically
    } catch (err) {
      console.error('Error updating profile:', err);
      // Just log error, don't show alert
    }
  };

  const handleCancel = () => {
    const detectedCountry = getCountryFromMobile(user?.mobile);
    setFormData({
      mobile: user?.mobile || '',
      email: user?.email || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      ownerName: user?.ownerName || '',
      companyName: user?.companyName || '',
      businessIndustry: user?.businessIndustry || '',
      address_line_1: user?.address_line_1 || '',
      address_line_2: user?.address_line_2 || '',
      city: user?.city || '',
      state: user?.state || '',
      country: user?.country || detectedCountry,
      zip_code: user?.zip_code || '',
    });
    setIsEditing(false);
  };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto animate-in fade-in duration-700">
      {/* Sticky Edit Button - Always visible when not editing */}
      {!isEditing && (
        <button
          className="fixed top-28 right-12 z-[9999] bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal p-4 rounded-full shadow-2xl hover:shadow-bulky-blue-500/50 transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center justify-center group"
          onClick={() => setIsEditing(true)}
          title="Edit Profile"
        >
          <span className="material-icons-round text-xl">edit</span>
          <span className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-bulky-navy-800 text-white text-xs px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
            Edit Profile
          </span>
        </button>
      )}

      {/* Sticky Save/Cancel Buttons - Visible when editing */}
      {isEditing && (
        <div className="fixed top-28 right-12 z-[9999] flex gap-2">
          <button
            className="bg-green-500 hover:bg-green-600 text-white font-normal px-4 py-3 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center justify-center group"
            onClick={handleSubmit}
            title="Save Changes"
          >
            <span className="material-icons-round text-lg">save</span>
            <span className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-bulky-navy-800 text-white text-xs px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
              Save Changes
            </span>
          </button>
          <button
            className="bg-gray-500 hover:bg-gray-600 text-white font-normal px-4 py-3 rounded-full shadow-2xl hover:shadow-gray-500/50 transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center justify-center group"
            onClick={handleCancel}
            title="Cancel"
          >
            <span className="material-icons-round text-lg">close</span>
            <span className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-bulky-navy-800 text-white text-xs px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
              Cancel
            </span>
          </button>
        </div>
      )}

      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden mb-6 relative">
        <div className="bg-gradient-to-br from-bulky-navy-800 via-bulky-navy-900 to-bulky-navy-950 p-6 md:p-8 text-center md:text-left relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-bulky-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
            <div className="relative group">
              <div
                className="w-20 h-20 md:w-24 md:h-24 rounded-xl border-2 border-white/10 shadow-xl bg-white/5 flex items-center justify-center text-white cursor-pointer hover:bg-white/10 transition-all duration-300 overflow-hidden"
                onClick={handleImageClick}
                title={user?.profile_image ? "Click to change profile image" : "Click to upload profile image"}
              >
                {user?.profile_image ? (
                  <img
                    key={`${user.profile_image}-${forceUpdate}`} // Force re-render when image changes
                    src={user.profile_image}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // If image fails to load, show initials instead
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}

                {/* Fallback to initials or upload prompt */}
                <div
                  className={`w-full h-full flex flex-col items-center justify-center ${user?.profile_image ? 'hidden' : 'flex'}`}
                  style={{ display: user?.profile_image ? 'none' : 'flex' }}
                >
                  {user?.firstName || user?.lastName ? (
                    // Show user initials
                    <div className="text-2xl md:text-3xl font-normal text-white">
                      {(user?.firstName?.[0] || '').toUpperCase()}{(user?.lastName?.[0] || '').toUpperCase()}
                    </div>
                  ) : (
                    // Show upload prompt if no name available
                    <>
                      <svg className="w-8 h-8 md:w-10 md:h-10 text-white/60 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-xs text-white/40">Upload</span>
                    </>
                  )}
                </div>
              </div>
              {/* Upload indicator */}
              <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-bulky-blue-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                </svg>
              </div>
            </div>

            <div className="flex-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full mb-3 border border-white/10">
                <span className="material-icons-round text-bulky-blue-400 text-xs">verified_user</span>
                <span className="text-xs font-normal text-white uppercase tracking-wider">{user?.role?.replace('_', ' ')}</span>
              </div>
              <h2 className="text-xl md:text-2xl font-medium text-white tracking-tight leading-tight">
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.name || user?.username}
              </h2>
              <p className="text-white/60 text-sm font-normal tracking-tight">@{user?.username}</p>

              <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-6 border-t border-white/5 pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-bulky-blue-400 border border-white/5">
                    <span className="material-icons-round text-sm">event</span>
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-0.5">Joined</p>
                    <p className="text-xs font-normal text-white">{user?.created_at ? new Date(parseInt(user.created_at)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-emerald-400 border border-white/5">
                    <span className="material-icons-round text-sm">history</span>
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-normal text-bulky-navy-400 uppercase tracking-wider mb-0.5">Last Update</p>
                    <p className="text-xs font-normal text-white">{user?.updated_at ? new Date(parseInt(user.updated_at)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full md:w-auto flex flex-col gap-2">
              {isEditing && (
                <>
                  <button
                    className="w-full md:w-auto bg-green-500 hover:bg-green-600 text-white font-normal px-6 py-3 rounded-xl transition-all shadow-lg transform active:scale-95 flex items-center justify-center gap-2 text-xs uppercase tracking-wider"
                    onClick={handleSubmit}
                  >
                    <span className="material-icons-round text-sm">save</span>
                    <span>Save Changes</span>
                  </button>
                  <button
                    className="w-full md:w-auto bg-gray-500 hover:bg-gray-600 text-white font-normal px-6 py-3 rounded-xl transition-all border transform active:scale-95 text-xs uppercase tracking-wider"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* Account Details Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <form onSubmit={handleSubmit}>
              <div className="mb-0">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-bulky-blue-50 rounded-xl flex items-center justify-center text-bulky-blue-500">
                    <span className="material-icons-round text-lg">contact_mail</span>
                  </div>
                  <div>
                    <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Account Details</h2>
                    <p className="text-xs font-normal text-gray-500 uppercase tracking-wider mt-1">Login & Contact Information</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Mobile */}
                  <div className="relative group">
                    <label className="text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1 flex items-center gap-2">
                      Mobile Number
                      <InfoTooltip message="This is your login key and cannot be changed for security reasons">
                        <span className="material-icons-round text-gray-400 text-sm">lock</span>
                      </InfoTooltip>
                    </label>
                    <div className="relative">
                      <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">phone_iphone</span>
                      <input
                        type="tel"
                        value={formData.mobile}
                        disabled={true} // Always disabled - it's the login key
                        className="w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Email with verification */}
                  <div className="relative group">
                    <label className="text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1 flex items-center">
                      Email Address
                      {user?.email_verified ? (
                        <InfoTooltip message="Email address has been verified and is secure">
                          <span className="ml-2 text-green-600 font-normal">✓ Verified</span>
                        </InfoTooltip>
                      ) : (
                        <InfoTooltip message="Email address needs verification for security">
                          <span className="ml-2 text-amber-600 font-normal">⚠ Unverified</span>
                        </InfoTooltip>
                      )}
                    </label>
                    <div className="relative">
                      <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors text-lg">alternate_email</span>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        disabled={!isEditing}
                        className={`w-full pl-12 pr-16 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                          ? 'border-gray-100 bg-white focus:border-bulky-blue-500 focus:outline-none shadow-sm'
                          : 'border-transparent bg-gray-50 text-bulky-navy-600'
                          }`}
                      />
                      {/* Show verify button when email is different from current OR when current email is unverified */}
                      {isEditing && formData.email && (
                        (formData.email !== user?.email) ||
                        (formData.email === user?.email && !user?.email_verified)
                      ) && (
                          <button
                            type="button"
                            onClick={handleEmailChange}
                            className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white px-3 py-1.5 rounded-lg hover:opacity-80 ${formData.email !== user?.email
                              ? 'bg-blue-500 hover:bg-blue-600'
                              : 'bg-amber-500 hover:bg-amber-600'
                              }`}
                          >
                            {formData.email !== user?.email ? 'Verify' : 'Verify'}
                          </button>
                        )}
                      {/* Show verify button for current email only if not verified and not editing */}
                      {!isEditing && user?.email && !user?.email_verified && formData.email === user?.email && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewEmail(user.email);
                            setShowEmailDialog(true);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs bg-amber-500 text-white px-3 py-1.5 rounded-lg hover:bg-amber-600"
                        >
                          Verify
                        </button>
                      )}
                    </div>
                  </div>

                  {/* First Name */}
                  <div className="relative group">
                    <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">First Name</label>
                    <div className="relative">
                      <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors text-lg">person</span>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        disabled={!isEditing}
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                          ? 'border-gray-100 bg-white focus:border-bulky-blue-500 focus:outline-none shadow-sm'
                          : 'border-transparent bg-gray-50 text-bulky-navy-600'
                          }`}
                      />
                    </div>
                  </div>

                  {/* Last Name */}
                  <div className="relative group">
                    <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">Last Name</label>
                    <div className="relative">
                      <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-bulky-blue-500 transition-colors text-lg">person</span>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        disabled={!isEditing}
                        className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                          ? 'border-gray-100 bg-white focus:border-bulky-blue-500 focus:outline-none shadow-sm'
                          : 'border-transparent bg-gray-50 text-bulky-navy-600'
                          }`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Business Information Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-500">
                <span className="material-icons-round text-lg">business</span>
              </div>
              <div>
                <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Business Information</h2>
                <p className="text-xs font-normal text-gray-500 uppercase tracking-wider mt-1">Company & Industry</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Owner Name */}
              <div className="relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">Owner Name</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors text-lg">account_circle</span>
                  <input
                    type="text"
                    value={formData.ownerName}
                    onChange={(e) => handleInputChange('ownerName', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                      ? 'border-gray-100 bg-white focus:border-bulky-blue-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600'
                      }`}
                  />
                </div>
              </div>

              {/* Company Name */}
              <div className="relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">Company Name</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors text-lg">domain</span>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                      ? 'border-gray-100 bg-white focus:border-bulky-blue-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600'
                      }`}
                  />
                </div>
              </div>

              {/* Business Industry */}
              <div className="md:col-span-2 relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">Business Industry</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors text-lg">category</span>
                  <input
                    type="text"
                    value={formData.businessIndustry}
                    onChange={(e) => handleInputChange('businessIndustry', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                      ? 'border-gray-100 bg-white focus:border-bulky-blue-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600'
                      }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address Information Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-500">
                <span className="material-icons-round text-lg">location_on</span>
              </div>
              <div>
                <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Address Information</h2>
                <p className="text-xs font-normal text-gray-500 uppercase tracking-wider mt-1">Location Details</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Address Line 1 */}
              <div className="md:col-span-2 relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">Address Line 1</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors text-lg">home</span>
                  <input
                    type="text"
                    value={formData.address_line_1}
                    onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                      ? 'border-gray-100 bg-white focus:border-green-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600'
                      }`}
                  />
                </div>
              </div>

              {/* Address Line 2 */}
              <div className="md:col-span-2 relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">Address Line 2</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors text-lg">home</span>
                  <input
                    type="text"
                    value={formData.address_line_2}
                    onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                      ? 'border-gray-100 bg-white focus:border-green-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600'
                      }`}
                  />
                </div>
              </div>

              {/* City */}
              <div className="relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">City</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors text-lg">location_city</span>
                  <select
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    disabled={!isEditing || !formData.state}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing && formData.state
                      ? 'border-gray-100 bg-white focus:border-green-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600 cursor-not-allowed'
                      }`}
                  >
                    <option value="">Select City</option>
                    {cities.map((city) => (
                      <option key={city.name} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* State */}
              <div className="relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">State</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors text-lg">map</span>
                  <select
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                      ? 'border-gray-100 bg-white focus:border-green-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600'
                      }`}
                  >
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.isoCode} value={state.isoCode}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Country */}
              <div className="relative group">
                <label className="text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1 flex items-center gap-2">
                  Country
                  <InfoTooltip message="Country is automatically detected from your mobile number's country code">
                    <span className="material-icons-round text-gray-400 text-sm">location_searching</span>
                  </InfoTooltip>
                </label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">public</span>
                  <input
                    type="text"
                    value={getCountryName(formData.country)}
                    disabled={true} // Always disabled - auto-detected from mobile
                    className="w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm border-transparent bg-gray-100 text-gray-600 cursor-not-allowed"
                    readOnly
                  />
                </div>
              </div>

              {/* ZIP Code */}
              <div className="relative group">
                <label className="block text-xs font-normal text-bulky-navy-600 uppercase tracking-wider mb-2 ml-1">ZIP Code</label>
                <div className="relative">
                  <span className="material-icons-round absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-500 transition-colors text-lg">markunread_mailbox</span>
                  <input
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => handleInputChange('zip_code', e.target.value)}
                    disabled={!isEditing}
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all font-normal text-sm ${isEditing
                      ? 'border-gray-100 bg-white focus:border-green-500 focus:outline-none shadow-sm'
                      : 'border-transparent bg-gray-50 text-bulky-navy-600'
                      }`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 space-y-6">
          <div className="bg-bulky-navy-50 rounded-2xl p-6 border border-bulky-navy-100/50 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-bulky-navy-600 shadow-sm border border-gray-100">
                <span className="material-icons-round text-lg">security</span>
              </div>
              <h2 className="text-lg font-medium text-bulky-navy-800 tracking-tight">Account Info</h2>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <p className="text-xs font-normal text-gray-500 uppercase tracking-wider mb-2">Username</p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-bulky-blue-50 flex items-center justify-center text-bulky-blue-500">
                    <span className="material-icons-round text-sm">alternate_email</span>
                  </div>
                  <span className="text-sm font-normal text-bulky-navy-800 uppercase">{user?.username}</span>
                </div>
              </div>

              <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <p className="text-xs font-normal text-gray-500 uppercase tracking-wider mb-2">Access Level</p>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-bulky-navy-50 flex items-center justify-center text-bulky-navy-600">
                    <span className="material-icons-round text-sm">admin_panel_settings</span>
                  </div>
                  <span className="text-sm font-normal text-bulky-navy-800 uppercase">{user?.role?.replace('_', ' ')}</span>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="p-6 bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 rounded-[1.5rem] text-white shadow-lg relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-12 -mt-12 blur-xl transition-all group-hover:bg-white/10"></div>
                <div className="flex items-center gap-2 mb-2 relative z-10">
                  <span className="material-icons-round text-sm">info</span>
                  <p className="text-xs font-normal uppercase tracking-wider">Notice</p>
                </div>
                <p className="text-sm font-normal leading-relaxed opacity-90 relative z-10">
                  {user?.role === 'super_admin'
                    ? 'Super Admin has full system access. Username and role are system-locked.'
                    : `${user?.role?.replace('_', ' ')} can edit profile fields when in editing mode.`
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Change Confirmation Dialog */}
      {showEmailDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="font-medium">Confirm Email Change</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to change your email from <strong>{user?.email}</strong> to <strong>{newEmail}</strong>?
            </p>
            <p className="text-sm text-amber-600 mb-6">
              You will need to verify the new email address with an OTP sent to your new email.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowEmailDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={emailLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleEmailConfirm}
                disabled={emailLoading}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {emailLoading ? 'Sending...' : 'Yes, Send OTP'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email OTP Verification Dialog */}
      {showOTPDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="font-medium">Verify Email Address</h2>
            <p className="text-gray-600 mb-4">
              Enter the 6-digit verification code sent to <strong>{newEmail}</strong>
            </p>
            <div className="mb-4">
              <input
                type="text"
                value={emailOTP}
                onChange={(e) => setEmailOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-mono tracking-wider"
                maxLength={6}
              />
              {emailOTP.length > 0 && emailOTP.length < 6 && (
                <p className="text-sm text-red-500 mt-2 text-center">Please enter all 6 digits</p>
              )}
            </div>
            {otpTimer > 0 && (
              <p className="text-sm text-gray-500 mb-4 text-center">
                Code expires in {formatTime(otpTimer)}
              </p>
            )}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowOTPDialog(false);
                  setEmailOTP('');
                  setOtpTimer(0);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={emailLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleOTPVerify}
                disabled={emailLoading || emailOTP.length !== 6}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
              >
                {emailLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto animate-zoom-in">
            {/* Header */}
            <div className="bg-red-500 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="material-icons-round text-white text-lg">error</span>
                </div>
                <h3 className="text-white font-semibold text-lg">Upload Error</h3>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 text-sm leading-relaxed">{errorMessage}</p>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-6 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium transition-all active:scale-95"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <ImageUploadDialogs
        showImageUploadDialog={showImageUploadDialog}
        setShowImageUploadDialog={setShowImageUploadDialog}
        showImageConfirmDialog={showImageConfirmDialog}
        setShowImageConfirmDialog={setShowImageConfirmDialog}
        imagePreview={imagePreview}
        imageFile={imageFile}
        imageUploading={imageUploading}
        user={user}
        handleImageFileSelect={handleImageFileSelect}
        handleImageUpload={handleImageUpload}
        handleImageDelete={handleImageDelete}
        setImageFile={setImageFile}
        setImagePreview={setImagePreview}
      />
    </div>
  );
};

export default ProfileManagement;